import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Paper, Divider, IconButton, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import SignatureCanvas from 'react-signature-canvas';
import { dev_baseUrl } from '../../utils/var';

const ApprovedPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const estimateID = queryParams.get('estimateID');
  const token = queryParams.get('token');
  const depositAmount = queryParams.get('DepositAmount');

  const sigCanvas = useRef(null);
  const [approved, setApproved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSignature, setShowSignature] = useState(true);
  const [signatureProvided, setSignatureProvided] = useState(false);
  const [error, setError] = useState('');

  // Don't auto-execute approval anymore
  // useEffect(() => {
  //   handleApproveAction();
  // }, [estimateID, token]);

  const clearSignature = () => {
    sigCanvas.current.clear();
    setSignatureProvided(false);
  };

  const handleSignatureEnd = () => {
    if (!sigCanvas.current.isEmpty()) {
      setSignatureProvided(true);
    }
  };

  const convertSignatureToPNG = () => {
    return new Promise((resolve, reject) => {
      try {
        const canvas = sigCanvas.current.getCanvas();
        
        // Create a new canvas with white background
        const newCanvas = document.createElement('canvas');
        newCanvas.width = canvas.width;
        newCanvas.height = canvas.height;
        const ctx = newCanvas.getContext('2d');
        
        // Fill white background
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, newCanvas.width, newCanvas.height);
        
        // Draw signature on top
        ctx.drawImage(canvas, 0, 0);
        
        // Convert to blob (PNG format - native canvas format)
        newCanvas.toBlob((blob) => {
          if (blob) {
            console.log('PNG Blob created:', blob.size, 'bytes', blob.type);
            resolve(blob);
          } else {
            reject(new Error('Failed to create image blob'));
          }
        }, 'image/png');
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleProceed = async () => {
    if (sigCanvas.current.isEmpty()) {
      setError('Please provide your signature before proceeding');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Step 1: Convert signature to PNG (native canvas format)
      const signatureBlob = await convertSignatureToPNG();
      
      // Create a File object from blob
      const signatureFile = new File(
        [signatureBlob], 
        `estimate_${estimateID}_signature.png`, 
        { type: 'image/png' }
      );

      console.log('Signature file created:', {
        name: signatureFile.name,
        type: signatureFile.type,
        size: signatureFile.size
      });

      // Step 2: Call new combined Approve API with signature file
      const formData = new FormData();
      formData.append('EstimateID', estimateID);
      formData.append('Token', token);
      formData.append('File', signatureFile);

      console.log('Calling approve API with signature...');

      const response = await fetch(`${dev_baseUrl}/Estimate/approve`, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header - browser will set it automatically with boundary
      });

      const data = await response.json();
      console.log('Approve API response:', data);

      if (response.ok && !data.isError) {
        setApproved(true);
        setShowSignature(false);
      } else {
        setError(data.message || 'Failed to approve estimate. Please contact support.');
      }
    } catch (error) {
      console.error('Error in approval process:', error);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        textAlign: 'center',
        padding: 3,
        background: 'linear-gradient(to bottom right, #daf0ff, #e3ebf2, #cbf5dd)'
      }}>
        <CircularProgress size={60} sx={{ mb: 3 }} />
        <Typography variant="h6">Processing your approval...</Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          Please wait while we save your signature and approve the estimate
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '80vh',
      textAlign: 'center',
      padding: 3,
      height:'100vh',
      background: 'linear-gradient(to bottom right, #daf0ff, #e3ebf2, #cbf5dd)'
    }}>
      <Paper 
        elevation={3}
        sx={{
          padding: 4,
          maxWidth: 500,
          width: '100%',
          borderRadius: 2,
          position: 'relative'
        }}
      >
        {/* Close Icon for both dialogs */}
        <IconButton 
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary'
          }}
        >
          <CloseIcon />
        </IconButton>

        {approved ? (         
          // SUCCESS SCREEN
          <>
            <CheckCircleIcon 
              sx={{ 
                fontSize: 80, 
                mb: 3,
                color: '#23A26D'
              }} 
            />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Estimate Approved
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, fontSize: '1.1rem' }}>
              Estimate #{estimateID} has been successfully approved.
            </Typography> 
            {depositAmount && depositAmount > 0 && (
              <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                <Box variant="body1" sx={{display: 'flex', mb: 2, fontSize: '1.1rem' }}>
                  {`Deposit Required: `} 
                  <Box sx={{ color: '#28a745', fontSize: '1.2rem', fontWeight: 700, ml:2 }}>
                    ${parseFloat(depositAmount).toFixed(2)}
                  </Box>
                </Box>
              </Box>              
            )}                        
            <Button
              variant="contained"
              color='main'
              fullWidth
              onClick={() => navigate('/dashboard')}
              sx={{                
                py: 1.2,
                mt: 2,
                mb: 3,
                fontSize: '1.1rem',
                fontWeight: 600
              }}
            >
              Return to Dashboard
            </Button>
          </>
        ) : showSignature ? (
          // SIGNATURE CAPTURE SCREEN
          <>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
              Approve Estimate #{estimateID}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              Please provide your signature to approve this estimate
            </Typography>
            
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ 
              border: '2px solid #ddd', 
              borderRadius: 2, 
              mb: 2,
              backgroundColor: '#fafafa',
              overflow: 'hidden'
            }}>
              <SignatureCanvas
                ref={(ref) => {
                  if (ref && sigCanvas) {
                    sigCanvas.current = ref;
                  }
                }}
                canvasProps={{
                  width: 500,
                  height: 200,
                  className: 'signature-canvas',
                  style: { width: '100%', height: '200px' }
                }}
                onEnd={handleSignatureEnd}
                backgroundColor='#ffffff'
              />
            </Box>

            {error && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
              <Button
                variant="outlined"
                onClick={clearSignature}
                sx={{ minWidth: 120 }}
              >
                Clear
              </Button>
              <Button
                variant="contained"
                color='main'
                onClick={handleProceed}
                disabled={!signatureProvided}
                sx={{ 
                  minWidth: 180,
                  py: 1.2,
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                Proceed to Approve
              </Button>
            </Box>

            {depositAmount && depositAmount > 0 && (
              <Box sx={{ 
                mt: 3, 
                p: 2, 
                backgroundColor: '#f0f9ff', 
                borderRadius: 2,
                border: '1px solid #b3d9ff'
              }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                  Deposit Required
                </Typography>
                <Typography variant="h5" sx={{ color: '#28a745', fontWeight: 700 }}>
                  ${parseFloat(depositAmount).toFixed(2)}
                </Typography>
              </Box>
            )}
          </>
        ) : (
          // ERROR SCREEN
          <>
            <ErrorIcon 
              color="error" 
              sx={{ 
                fontSize: 80, 
                mb: 3
              }} 
            />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
              Action Unavailable
            </Typography>
            <Typography variant="body2" sx={{ mb: 4, fontSize: '1.1rem' }}>
              Estimate #{estimateID} cannot be approved in its current state.
              Please check the status or contact support.
            </Typography>
            
            <Button
              variant="contained"
              fullWidth
              color='main'
              onClick={() => navigate('/dashboard')}
              sx={{ 
                minWidth: 200,
                py: 1.2,
                fontSize: '1.1rem',
                mt: 2,
                mb: 3,
                fontWeight: 600
              }}
            >
              Return to Dashboard
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ApprovedPage;